import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Alert, AlertDescription } from "../components/ui/alert";
import { toast } from "sonner";
import {
  Briefcase,
  ArrowRight,
  CheckCircle,
  Warning,
  SpinnerGap,
  ShieldCheck,
  UserSwitch,
  SignOut,
  XCircle,
} from "phosphor-react";
import { bumpAuthVersion, hardAuthReset } from "../utils/authSignal";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * AcceptInvite - Page for technicians to accept hire-away invitations
 * 
 * Route: /accept-invite?token=...
 * 
 * Flow:
 * 1. Fetch invitation info AND current session user in parallel
 * 2. SESSION GATE: If logged in as different user, show "Switch Account" gate
 * 3. Show confirmation with contractor name
 * 4. If hire-away: show warning about moving from current contractor
 * 5. On accept: call API, redirect to tech portal
 * 
 * SESSION GATE (P0 - prevents identity confusion):
 * - If session user email != invited email: BLOCK with explicit gate
 * - User must explicitly switch accounts (logout + reload)
 * - Prevents contractor seeing tech dashboard or vice versa
 */
export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Session gate state
  const [sessionUser, setSessionUser] = useState(null);
  const [sessionMismatch, setSessionMismatch] = useState(false);
  const [switchingAccount, setSwitchingAccount] = useState(false);

  /**
   * Fetch current session user (if any)
   * Uses the same auth pattern as the rest of the app
   */
  const fetchSessionUser = useCallback(async () => {
    try {
      const authToken = localStorage.getItem("auth_token") || localStorage.getItem("token");
      const response = await fetch(`${API}/auth/me`, {
        credentials: "include",
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      });
      
      if (response.ok) {
        const data = await response.json();
        return data;
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  /**
   * Compare emails case-insensitively
   */
  const emailsMatch = (email1, email2) => {
    if (!email1 || !email2) return false;
    return email1.toLowerCase().trim() === email2.toLowerCase().trim();
  };

  /**
   * Handle "Switch Account" - logout current session and reload invite page
   */
  const handleSwitchAccount = async () => {
    setSwitchingAccount(true);
    try {
      // 1. Bump auth version to notify other tabs BEFORE logout
      bumpAuthVersion('session_switch', null);
      
      // 2. Call backend logout
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
      
      // 3. CRITICAL: Clear ALL cached data (auth + domain data)
      hardAuthReset();
      
      // 4. Hard reload to same URL to ensure clean identity hydration
      // This prevents any stale state from persisting
      window.location.replace(window.location.href);
    } catch (err) {
      console.error("[AcceptInvite] Switch account failed:", err);
      toast.error("Failed to switch account. Please try again.");
      setSwitchingAccount(false);
    }
  };

  useEffect(() => {
    if (!token) {
      setError("No invitation token provided");
      setLoading(false);
      return;
    }
    
    // Fetch both invitation info and session user in parallel
    const initPage = async () => {
      try {
        const [inviteResponse, currentUser] = await Promise.all([
          fetch(`${API}/hire-away/invitation/${token}`),
          fetchSessionUser(),
        ]);
        
        // Process invitation
        const inviteData = await inviteResponse.json();
        
        if (!inviteResponse.ok) {
          setError(inviteData.detail || inviteData.message || "Invalid invitation");
          return;
        }
        
        if (!inviteData.valid) {
          setError(inviteData.message || "Invitation is no longer valid");
          return;
        }
        
        setInvitation(inviteData);
        setSessionUser(currentUser);
        
        // SESSION GATE CHECK
        // If user is logged in AND emails don't match -> show gate
        if (currentUser && currentUser.email && inviteData.email) {
          if (!emailsMatch(currentUser.email, inviteData.email)) {
            setSessionMismatch(true);
          }
        }
        
      } catch (err) {
        console.error("[AcceptInvite] Init failed:", err);
        setError("Failed to load invitation details");
      } finally {
        setLoading(false);
      }
    };
    
    initPage();
  }, [token, fetchSessionUser]);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      const response = await fetch(`${API}/hire-away/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (!response.ok) {
        toast.error(data.detail || "Failed to accept invitation");
        setAccepting(false);
        return;
      }

      setSuccess(true);
      toast.success(data.message || "Successfully joined!");

      // If user needs to set password, redirect to activate page
      if (data.needs_password) {
        setTimeout(() => {
          navigate(`/activate?token=${token}`);
        }, 1500);
        return;
      }

      // Store auth token if provided
      if (data.token) {
        localStorage.setItem("token", data.token);
      }

      // Redirect to tech portal
      setTimeout(() => {
        navigate("/tech");
      }, 1500);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-2 border-zinc-200">
          <CardContent className="py-12 text-center">
            <SpinnerGap className="animate-spin h-8 w-8 mx-auto text-zinc-400" />
            <p className="mt-4 text-zinc-500">Loading invitation...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-2 border-red-200">
          <CardContent className="py-8">
            <div className="text-center">
              <Warning size={48} className="mx-auto text-red-500 mb-4" />
              <h2 className="text-xl font-semibold mb-2">Invalid Invitation</h2>
              <p className="text-zinc-600 mb-6">{error}</p>
              <Button
                variant="outline"
                onClick={() => navigate("/")}
                className="border-2"
              >
                Go to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-2 border-emerald-200">
          <CardContent className="py-12 text-center">
            <CheckCircle size={64} className="mx-auto text-emerald-500 mb-4" />
            <h2 className="text-2xl font-semibold text-emerald-700 mb-2">
              Welcome to {invitation?.contractor_name}!
            </h2>
            <p className="text-zinc-600">Redirecting you to your dashboard...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ============================================
  // SESSION MISMATCH GATE (P0 - prevents identity confusion)
  // ============================================
  // If user is logged in as a DIFFERENT email than the invitation,
  // we MUST show a blocking gate requiring explicit account switch.
  // This prevents the scenario where contractor accepts a tech invite
  // and sees mixed identity/menu state.
  if (sessionMismatch && sessionUser && invitation) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-2 border-amber-300" data-testid="session-mismatch-gate">
          <CardHeader className="text-center border-b border-amber-100 pb-6 bg-amber-50/50">
            <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
              <UserSwitch size={32} className="text-amber-600" />
            </div>
            <CardTitle className="text-xl font-heading">
              Different Account Detected
            </CardTitle>
            <CardDescription className="text-base mt-2 text-amber-800">
              You need to switch accounts to accept this invitation
            </CardDescription>
          </CardHeader>

          <CardContent className="py-6 space-y-6">
            {/* Current session info */}
            <div className="bg-zinc-100 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-500">Currently logged in as</span>
                <span className="font-medium text-zinc-800" data-testid="current-session-email">
                  {sessionUser.email}
                </span>
              </div>
              <div className="border-t border-zinc-200 pt-3 flex items-center justify-between">
                <span className="text-sm text-zinc-500">Invitation is for</span>
                <span className="font-medium text-amber-700" data-testid="invited-email">
                  {invitation.email}
                </span>
              </div>
            </div>

            {/* Warning message */}
            <Alert className="border-amber-300 bg-amber-50">
              <Warning className="h-5 w-5 text-amber-600" />
              <AlertDescription className="text-amber-800">
                To accept this invitation, you must log out of your current account and log in as{" "}
                <span className="font-semibold">{invitation.email}</span>.
              </AlertDescription>
            </Alert>

            {/* Actions */}
            <div className="flex flex-col gap-3 pt-2">
              <Button
                onClick={handleSwitchAccount}
                disabled={switchingAccount}
                className="w-full h-12 bg-amber-600 hover:bg-amber-700 text-white text-lg"
                data-testid="switch-account-btn"
              >
                {switchingAccount ? (
                  <>
                    <SpinnerGap className="animate-spin mr-2" size={20} />
                    Switching account...
                  </>
                ) : (
                  <>
                    <SignOut size={20} className="mr-2" />
                    Continue &amp; Switch Account
                  </>
                )}
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate(-1)}
                className="text-zinc-500"
                disabled={switchingAccount}
                data-testid="cancel-switch-btn"
              >
                <XCircle size={16} className="mr-2" />
                Cancel
              </Button>
            </div>

            <p className="text-xs text-center text-zinc-400">
              Clicking &quot;Continue &amp; Switch Account&quot; will log you out and bring you back to this invitation.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-2 border-zinc-200">
        <CardHeader className="text-center border-b border-zinc-100 pb-6">
          <div className="mx-auto w-16 h-16 bg-[#FF6600]/10 rounded-full flex items-center justify-center mb-4">
            <Briefcase size={32} className="text-[#FF6600]" />
          </div>
          <CardTitle className="text-2xl font-heading uppercase">
            You&apos;re Invited!
          </CardTitle>
          <CardDescription className="text-base mt-2">
            Join <span className="font-semibold text-zinc-800">{invitation?.contractor_name}</span> as a technician
          </CardDescription>
        </CardHeader>

        <CardContent className="py-6 space-y-6">
          {/* Hire-away warning */}
          {invitation?.is_hire_away && (
            <Alert className="border-amber-300 bg-amber-50">
              <UserSwitch className="h-5 w-5 text-amber-600" />
              <AlertDescription className="text-amber-800">
                <span className="font-semibold">Note:</span> This will move you from{" "}
                <span className="font-medium">{invitation.current_contractor_name}</span>.
                You can only belong to one contractor at a time.
              </AlertDescription>
            </Alert>
          )}

          {/* Invitation details */}
          <div className="bg-zinc-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-zinc-500">Email</span>
              <span className="font-medium">{invitation?.email}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-zinc-500">Role</span>
              <Badge variant="outline" className="border-[#FF6600] text-[#FF6600]">
                Technician
              </Badge>
            </div>
            {invitation?.is_hire_away && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-500">Transfer</span>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-zinc-600">{invitation.current_contractor_name}</span>
                  <ArrowRight size={14} className="text-zinc-400" />
                  <span className="font-medium text-zinc-800">{invitation.contractor_name}</span>
                </div>
              </div>
            )}
          </div>

          {/* Trust indicators */}
          <div className="flex items-center gap-2 text-sm text-zinc-500">
            <ShieldCheck size={16} className="text-emerald-500" />
            <span>Secure invitation verified</span>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-3 pt-2">
            <Button
              onClick={handleAccept}
              disabled={accepting}
              className="w-full h-12 bg-[#FF6600] hover:bg-[#CC5200] text-white text-lg"
              data-testid="accept-invite-btn"
            >
              {accepting ? (
                <>
                  <SpinnerGap className="animate-spin mr-2" size={20} />
                  Accepting...
                </>
              ) : (
                <>
                  <CheckCircle size={20} className="mr-2" />
                  Accept & Join
                </>
              )}
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate("/")}
              className="text-zinc-500"
              disabled={accepting}
            >
              Cancel
            </Button>
          </div>

          <p className="text-xs text-center text-zinc-400">
            By accepting, you agree to join {invitation?.contractor_name}&apos;s team.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
